import { v4 as uuid } from '@lukeed/uuid';
import { parse, stringify } from 'qs';
import { APIError } from './api-error';

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

async function processResponse<TRes>(rawResponse: Response): Promise<TRes> {
  const contentType = rawResponse.headers.get('Content-Type');

  switch (contentType) {
    case 'application/json':
      return (await rawResponse.json()) as Promise<TRes>;
    case 'application/pdf':
      return (await rawResponse.blob()) as unknown as Promise<TRes>;
    case 'multipart/form-data':
    case 'application/x-www-form-urlencoded':
      return (await rawResponse.formData()) as unknown as Promise<TRes>;
    default: {
      if (contentType?.startsWith('image')) {
        return (await rawResponse.blob()) as unknown as Promise<TRes>;
      }

      return (await rawResponse.text()) as unknown as Promise<TRes>;
    }
  }
}

export interface TypedRequestInit<TReqBody> extends Omit<RequestInit, 'body'> {
  body: TReqBody;
}

function prepareParameters(params: any | undefined): any {
  if (params === '' || params === undefined || params === null) {
    return null;
  }

  if (Array.isArray(params)) {
    return params.reduce((accum, curr) => {
      const prepared = prepareParameters(curr);
      if (prepared) {
        accum.push(prepared);
      }

      return accum;
    }, []);
  }

  if (params instanceof Date) {
    return params;
  }

  if (typeof params === 'object') {
    return Object.entries(params || {}).reduce(
      (accum, [k, v]) => {
        if (!['', null, undefined].includes(v as any)) {
          accum[k] = prepareParameters(v);
        }

        return accum;
      },
      {} as Record<string, any>,
    );
  }

  return params;
}

export function parseSearchString<T>(search: string | undefined): T | undefined {
  if (!search) {
    return undefined;
  }

  return parse(search, { ignoreQueryPrefix: true, allowDots: true, parseArrays: true }) as unknown as T;
}

export function buildSearchString(params: any | undefined): string {
  const preparedParams = prepareParameters(params);
  if (!preparedParams) {
    return '';
  }

  // Convert objects to JSON strings that jsonapi can parse
  if (typeof preparedParams === 'object') {
    Object.keys(preparedParams).forEach((key) => {
      if (typeof preparedParams[key] === 'object' && !(preparedParams[key] instanceof Date)) {
        preparedParams[key] = JSON.stringify(preparedParams[key]);
      }
    });
  }

  return stringify(preparedParams, { addQueryPrefix: true, arrayFormat: 'repeat', allowDots: true, skipNulls: true });
}

export function buildMergedRequestInit<TReq = undefined>(
  method: HTTPMethod,
  basePath: string,
  unboundPath: string,
  params?: TReq,
  requestInit?: RequestInit,
): [HTTPMethod, string, TypedRequestInit<TReq> | undefined] {
  const paramCopy = JSON.parse(JSON.stringify(params || {}));
  let pathname = unboundPath;

  if (basePath) {
    pathname = new URL(unboundPath, basePath).pathname;
  }

  const urlSegments = pathname.split('/');

  for (let i = 0; i < urlSegments.length; i += 1) {
    const segment = urlSegments[i];
    if (segment.startsWith(':')) {
      const key = segment.slice(1);
      urlSegments[i] = paramCopy[key];
      delete paramCopy[key];
    }
  }

  const fullPath = `${basePath}${urlSegments.join('/')}`;

  if (method === 'GET') {
    return [method, `${fullPath}${buildSearchString(paramCopy)}`, requestInit as TypedRequestInit<TReq>];
  }

  return [method, fullPath, { ...(requestInit || {}), body: paramCopy }];
}

export async function makeRequest<TRes, TReqBody = undefined>(
  method: HTTPMethod,
  path: string,
  request?: TypedRequestInit<TReqBody>,
): Promise<TRes | undefined> {
  const { body, headers, ...requestInit } = request || {};
  const traceId = uuid();

  const requestOptions: RequestInit = {
    method,
    credentials: 'include',
    mode: 'cors',
    ...requestInit,
    headers: new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Trace': traceId,
      ...headers,
    }),
  };

  if (body) {
    requestOptions.body = JSON.stringify(body);
  }

  const response = await fetch(path, requestOptions);

  const responseBody = await processResponse<TRes>(response);

  if (!response.ok) {
    throw new APIError(response.status, `${method}:${path}`, traceId, responseBody);
  }

  return responseBody;
}

export function makeCancellableRequest<TRes, TReqBody = undefined>(
  method: HTTPMethod,
  path: string,
  request?: TypedRequestInit<TReqBody>,
): [Promise<TRes | undefined>, AbortController] {
  const controller = new AbortController();
  return [makeRequest(method, path, { ...request, signal: controller.signal } as TypedRequestInit<TReqBody>), controller];
}
